import { useScrollListener } from '@helpers/hooks/use-scroll-listener/useScrollListener';
import { ListDivider } from '@terminal/components/list-divider/ListDivider';
import { NewsThumbnail } from '@terminal/components/news-thumbnail/NewsThumbnail';
import { NewsThumbnailSkeleton } from '@terminal/components/news-thumbnail-skeleton/NewsThumbnailSkeleton';
import { useArticleDrawer } from '@terminal/hooks/use-article-drawer/useArticleDrawer';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useSearchNews } from '@terminal/views/news/hooks/use-search-news/useSearchNews';
import { createNewsApiPayload } from '@terminal/views/news/utils/create-news-api-payload/createNewsApiPayload';
import { ArticleDrawer } from '@terminal/widgets/news-widget/ArticleDrawer';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useNewsSearchStore } from '../../stores/use-news-search-store/useNewsSearchStore';
import { getFiltersFromActiveOptions, getUniqueNews } from '../../utils/utils';
import { NewsLoadingState } from '../news-loading-state/NewsLoadingState';
import { SearchEmptyStates } from '../search-empty-states/SearchEmptyStates';
import { SearchFilters } from '../search-filters/SearchFilters';
import { SortDropdown } from '../sort-dropdown/SortDropdown';
import styles from './SearchResults.module.scss';

export interface SearchResultsProps {
  searchQuery: string;
}

export const SearchResults = ({ searchQuery }: SearchResultsProps) => {
  const { t } = useTranslation('news');
  const { closeDrawer, singleNewsId, handleSelectSingleNews } =
    useArticleDrawer(POSTHOG_TRACKING_SOURCES.NEWS);

  const containerRef = useRef<HTMLDivElement>(null);
  const { accordionFilters, activeFilterOptions, sortBy, clearActiveFilters } =
    useNewsSearchStore(state => ({
      activeFilterOptions:
        state.filterGroups.NEWS_SEARCH_FILTERS.activeFilterOptions,
      accordionFilters: state.accordionFilters,
      sortBy: state.sortBy,
      clearActiveFilters: state.clearActiveFilters,
    }));

  const {
    data,
    fetchNextPage,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useSearchNews({
    query: searchQuery,
    sort_by: sortBy,
    filters: {
      ...getFiltersFromActiveOptions(activeFilterOptions),
      ...createNewsApiPayload(accordionFilters),
    },
  });

  const uniqueNews = getUniqueNews(data?.pages);
  const isResultsLoading = isLoading || isFetching;

  useEffect(() => {
    return () => {
      clearActiveFilters(t);
    };
  }, [clearActiveFilters]);

  useScrollListener(
    containerRef,
    () => {
      if (hasNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },

    [hasNextPage],
    { isGlobalScrollListened: true, offset: 40 }
  );

  return (
    <div
      className={styles.searchResultsRoot}
      data-testid="search-results"
      ref={containerRef}
    >
      <div className={styles.filtersWrapper}>
        <div className={styles.filters}>
          <SearchFilters />
          <SortDropdown />
        </div>
      </div>

      <div className={styles.list}>
        {isResultsLoading && <NewsLoadingState />}

        {!isResultsLoading && !!searchQuery && !uniqueNews.length && (
          <SearchEmptyStates />
        )}

        {uniqueNews.map(singleNews => (
          <NewsThumbnail
            key={singleNews.id}
            newsData={singleNews}
            onClick={handleSelectSingleNews}
            highlighted={searchQuery}
          />
        ))}

        {hasNextPage && isFetchingNextPage && <NewsThumbnailSkeleton />}
        {!hasNextPage && !isResultsLoading && !!uniqueNews.length && (
          <ListDivider label={t('news:endOfResults')} />
        )}
      </div>

      {!!singleNewsId && (
        <ArticleDrawer
          newsId={singleNewsId}
          source={POSTHOG_TRACKING_SOURCES.NEWS}
          list={uniqueNews}
          onClick={handleSelectSingleNews}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
};
